import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import firestoreService from '../services/firestoreService';
import AdminHeader from '../components/admin/AdminHeader';
import AdminNavigation from '../components/admin/AdminNavigation';
import './UserManagement.css';

const UserManagement = () => {
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [selectedUser, setSelectedUser] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('view'); // 'view', 'edit', 'create'
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [deleteFromAuth, setDeleteFromAuth] = useState(false);
    const [formData, setFormData] = useState({
        uid: '',
        email: '',
        displayName: '',
        createdAt: '',
        lastLoginAt: '',
        isActive: true,
        role: 'user'
    });
    const navigate = useNavigate();

    useEffect(() => {
        // Check authentication
        const isAuthenticated = localStorage.getItem('adminAuth');
        if (!isAuthenticated) {
            navigate('/admin');
            return;
        }

        loadUsers();
    }, [navigate]);

    useEffect(() => {
        // Filter users based on search term and status
        const filtered = users.filter(user => {
            const matchesSearch = (user.email && user.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (user.phone && user.phone.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (user.displayName && user.displayName.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (user.name && user.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
                user.uid.toLowerCase().includes(searchTerm.toLowerCase());

            const matchesStatus = statusFilter === 'all' ||
                (statusFilter === 'active' && user.isActive) ||
                (statusFilter === 'inactive' && !user.isActive);

            return matchesSearch && matchesStatus;
        });
        setFilteredUsers(filtered);
    }, [users, searchTerm, statusFilter]);

    const loadUsers = async () => {
        try {
            setLoading(true);
            const usersData = await firestoreService.getUsers();
            setUsers(usersData);
        } catch (error) {
            console.error('Error loading users:', error);
            setUsers([]);
        } finally {
            setLoading(false);
        }
    };

    const handleUserAction = (action, user = null) => {
        setModalMode(action);
        if (user) {
            setSelectedUser(user);
            setFormData({
                uid: user.uid,
                email: user.email,
                displayName: user.displayName,
                createdAt: user.createdAt,
                lastLoginAt: user.lastLoginAt,
                isActive: user.isActive,
                role: user.role
            });
        } else {
            setFormData({
                uid: '',
                email: '',
                displayName: '',
                createdAt: '',
                lastLoginAt: '',
                isActive: true,
                role: 'user'
            });
        }
        setShowModal(true);
    };

    const handleSaveUser = async () => {
        try {
            if (modalMode === 'create') {
                await firestoreService.createUser(formData);
            } else if (modalMode === 'edit') {
                await firestoreService.updateUser(formData.uid, formData);
            }
            setShowModal(false);
            loadUsers(); // Refresh the list
        } catch (error) {
            console.error('Error saving user:', error);
        }
    };

    const handleDeleteClick = (user) => {
        setUserToDelete(user);
        setShowDeleteModal(true);
        setDeleteFromAuth(false); // Reset checkbox
    };

    const handleDeleteUser = async () => {
        if (!userToDelete) return;

        try {
            // Use auth type from Firebase Auth data
            const authType = userToDelete.authType?.toLowerCase() || 'unknown';

            await firestoreService.deleteUser(userToDelete.uid, deleteFromAuth, authType);
            loadUsers(); // Refresh the list
            setShowDeleteModal(false);
            setUserToDelete(null);

            // Show success message
            if (deleteFromAuth) {
                alert('User deleted successfully from both Firestore and Firebase Authentication');
            } else {
                alert('User deleted successfully from Firestore');
            }
        } catch (error) {
            console.error('Error deleting user:', error);

            // Show more detailed error message
            let errorMessage = 'Failed to delete user: ' + error.message;
            if (error.message && error.message.includes('Service account needs IAM permissions')) {
                errorMessage += '\n\nPlease check the backend server logs for more details.';
            }

            alert(errorMessage);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) {
            return 'NA';
        }

        let date;
        if (dateString && dateString.toDate) {
            date = dateString.toDate();
        } else {
            date = new Date(dateString);
        }

        // Check if date is invalid or is 01/01/1970 (Unix epoch)
        if (isNaN(date.getTime()) || date.getTime() === 0) {
            return 'NA';
        }

        // Check if date is 01/01/1970 (common invalid date)
        const epochDate = new Date('1970-01-01T00:00:00.000Z');
        if (date.getTime() === epochDate.getTime()) {
            return 'NA';
        }

        return date.toLocaleString();
    };

    const getStatusBadge = (user) => {
        const isActive = user.isActive;
        const daysSinceActivity = user.daysSinceActivity || 0;

        return (
            <span className={`status-badge ${isActive ? 'active' : 'inactive'}`}>
                {isActive ? 'Active' : 'Inactive'}
                {!isActive && daysSinceActivity > 0 && (
                    <span className="status-detail">
                        ({daysSinceActivity}d ago)
                    </span>
                )}
            </span>
        );
    };

    const getAuthTypeDisplay = (user) => {
        return user.authType || 'Unknown';
    };

    const getAuthTypeClass = (user) => {
        const authType = user.authType || 'Unknown';
        switch (authType.toLowerCase()) {
            case 'email':
            case 'password':
                return 'email-auth';
            case 'phone':
                return 'phone-auth';
            case 'google':
                return 'google-auth';
            case 'apple':
                return 'apple-auth';
            default:
                return 'unknown-auth';
        }
    };

    if (loading) {
        return (
            <div className="user-management">
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Loading users...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="user-management">
            <AdminHeader title="User Management" />
            <AdminNavigation />

            <main className="admin-main">
                <div className="user-management-header">
                    <div className="search-section">
                        <input
                            type="text"
                            placeholder="Search users by email, phone, name, or ID..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="search-input"
                        />
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="status-filter"
                        >
                            <option value="all">All Users</option>
                            <option value="active">Active Only</option>
                            <option value="inactive">Inactive Only</option>
                        </select>
                    </div>
                    <button
                        className="create-user-button"
                        onClick={() => handleUserAction('create')}
                    >
                        + Add New User
                    </button>
                </div>

                <div className="users-table-container">
                    <table className="users-table">
                        <thead>
                            <tr>
                                <th>User ID</th>
                                <th>Email</th>
                                <th>Phone</th>
                                <th>Display Name</th>
                                <th>Auth Type</th>
                                <th>Status</th>
                                <th>Last Accessed</th>
                                <th>Created At</th>
                                <th>Last Login</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredUsers.map(user => (
                                <tr key={user.uid}>
                                    <td>{user.uid}</td>
                                    <td>{user.email || '-'}</td>
                                    <td>{user.phone || '-'}</td>
                                    <td>{user.displayName || user.name || '-'}</td>
                                    <td>
                                        <span className={`auth-type-badge ${getAuthTypeClass(user)}`}>
                                            {getAuthTypeDisplay(user)}
                                        </span>
                                    </td>
                                    <td>{getStatusBadge(user)}</td>
                                    <td>{formatDate(user.lastActivityDate)}</td>
                                    <td>{formatDate(user.createdAt)}</td>
                                    <td>{formatDate(user.lastSignInTime || user.lastLoginAt)}</td>
                                    <td>
                                        <div className="action-buttons">
                                            <button
                                                className="action-btn view-btn"
                                                onClick={() => handleUserAction('view', user)}
                                            >
                                                View
                                            </button>
                                            <button
                                                className="action-btn edit-btn"
                                                onClick={() => handleUserAction('edit', user)}
                                            >
                                                Edit
                                            </button>
                                            <button
                                                className="action-btn delete-btn"
                                                onClick={() => handleDeleteClick(user)}
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {showModal && (
                    <div className="modal-overlay">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h2>
                                    {modalMode === 'create' ? 'Create New User' :
                                        modalMode === 'edit' ? 'Edit User' : 'User Details'}
                                </h2>
                                <button
                                    className="close-button"
                                    onClick={() => setShowModal(false)}
                                >
                                    ×
                                </button>
                            </div>

                            <div className="modal-body">
                                <div className="form-group">
                                    <label>User ID</label>
                                    <input
                                        type="text"
                                        value={formData.uid}
                                        onChange={(e) => setFormData({ ...formData, uid: e.target.value })}
                                        disabled={modalMode === 'view'}
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Email</label>
                                    <input
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                        disabled={modalMode === 'view'}
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Display Name</label>
                                    <input
                                        type="text"
                                        value={formData.displayName}
                                        onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                                        disabled={modalMode === 'view'}
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Role</label>
                                    <select
                                        value={formData.role}
                                        onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                                        disabled={modalMode === 'view'}
                                    >
                                        <option value="user">User</option>
                                        <option value="admin">Admin</option>
                                        <option value="moderator">Moderator</option>
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={formData.isActive}
                                            onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                            disabled={modalMode === 'view'}
                                        />
                                        Active User
                                    </label>
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button
                                    className="cancel-button"
                                    onClick={() => setShowModal(false)}
                                >
                                    Cancel
                                </button>
                                {modalMode !== 'view' && (
                                    <button
                                        className="save-button"
                                        onClick={handleSaveUser}
                                    >
                                        {modalMode === 'create' ? 'Create User' : 'Save Changes'}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Delete Confirmation Modal */}
                {showDeleteModal && (
                    <div className="modal-overlay">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h2>Delete User</h2>
                                <button
                                    className="close-button"
                                    onClick={() => setShowDeleteModal(false)}
                                >
                                    ×
                                </button>
                            </div>

                            <div className="modal-body">
                                <div className="delete-warning">
                                    <p><strong>⚠️ Warning:</strong> This action cannot be undone!</p>
                                    <p>Are you sure you want to delete user <strong>{userToDelete?.email || userToDelete?.phone}</strong>?</p>
                                    <p className="auth-type-info">
                                        <strong>Authentication Type:</strong> {
                                            userToDelete?.authType || 'Unknown'
                                        }
                                    </p>
                                </div>

                                <div className="form-group">
                                    <label className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={deleteFromAuth}
                                            onChange={(e) => setDeleteFromAuth(e.target.checked)}
                                        />
                                        <span className="checkbox-text">
                                            Also delete from Firebase Authentication
                                        </span>
                                    </label>
                                    <p className="checkbox-description">
                                        If checked, the user will be completely removed from both Firestore and Firebase Auth ({userToDelete?.authType?.toLowerCase()} authentication).
                                        <br />
                                        If unchecked, only the Firestore document will be deleted.
                                    </p>
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button
                                    className="cancel-button"
                                    onClick={() => setShowDeleteModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="delete-confirm-button"
                                    onClick={handleDeleteUser}
                                >
                                    Delete User
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};

export default UserManagement;
